import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Button } from './ui/button';
import { Menu, X } from 'lucide-react';
import { useBooking } from '../context/BookingContext';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const { openBooking, openContact } = useBooking();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  const navLinks = [
    { label: 'Solutions', path: '/solutions' },
    { label: 'Features & Pricing', path: '/features-pricing' },
    { label: 'ROI Calculator', path: '/roi-calculator' },
    { label: 'AI Demo', path: '/ai-demo' },
    { label: 'Case Studies', path: '/case-studies' },
    { label: 'About', path: '/about' }
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <header 
      className="fixed top-0 left-0 right-0 z-50 transition-all duration-300 shadow-lg bg-primary-navy"
      data-testid="main-header"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <div className="flex items-center">
            <Link 
              to="/"
              className="flex items-center group"
              data-testid="logo-link"
            >
              <img 
                src="/getcare360-logo.png" 
                alt="GetCare 360" 
                className="h-8 md:h-10 w-auto"
              />
            </Link>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`font-medium transition-colors px-4 py-2 ${
                  isActive(link.path) 
                    ? 'text-teal' 
                    : 'text-white/90 hover:text-teal'
                }`}
                data-testid={`nav-${link.label.toLowerCase().replace(' ', '-')}`}
              >
                {link.label}
                {isActive(link.path) && (
                  <span className="block h-0.5 bg-[#D9792B] mt-1 rounded-full"></span>
                )}
              </Link>
            ))}
          </nav>

          {/* Desktop CTA */}
          <div className="hidden lg:flex items-center space-x-4">
            <Button
              onClick={openContact}
              variant="outline"
              className="border-white/30 text-white hover:bg-white hover:text-primary-navy"
              data-testid="nav-contact-btn"
            >
              Contact Us
            </Button>
            <Button
              onClick={openBooking}
              className="bg-primary-blue hover:bg-primary-blue/90 text-white"
              data-testid="nav-demo-btn"
            >
              Book a Demo
            </Button>
          </div>

          {/* Mobile Menu Button - 44x44px touch target for accessibility */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="lg:hidden w-11 h-11 flex items-center justify-center rounded-md text-white hover:bg-white/10 transition-colors"
            data-testid="mobile-menu-btn"
            aria-label={isMobileMenuOpen ? "Close menu" : "Open menu"}
          >
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="lg:hidden py-4 border-t border-white/10 animate-in slide-in-from-top">
            <nav className="flex flex-col space-y-1">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`px-4 py-3 rounded-md transition-colors ${
                    isActive(link.path) 
                      ? 'bg-primary-blue/20 text-teal' 
                      : 'text-white hover:bg-white/10'
                  }`}
                  data-testid={`mobile-nav-${link.label.toLowerCase().replace(' ', '-')}`}
                >
                  {link.label}
                </Link>
              ))}
              <div className="flex flex-col space-y-2 px-4 pt-4">
                <Button
                  onClick={() => { openContact(); setIsMobileMenuOpen(false); }}
                  variant="outline"
                  className="w-full border-white/30 text-white hover:bg-white hover:text-primary-navy"
                >
                  Contact Us
                </Button>
                <Button
                  onClick={() => { openBooking(); setIsMobileMenuOpen(false); }}
                  className="w-full bg-primary-blue hover:bg-primary-blue/90 text-white"
                >
                  Book a Demo
                </Button>
              </div>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
